import type { CREDIT_UNIT } from '../constants/payment.ts'
import type { DocumentWithAuthor, DocumentWithTimestamps } from './firebase.ts'
import type { OrgId, UserId } from './ids.ts'
import type { JobId } from './Job.ts'
import type { Org } from './Org.ts'
import type { TPaymentMethod } from './payment.ts'
import type { UserProfile } from './User.ts'

export type OrgTransactionId = string

// Enums

export enum OrgTransactionStatus {
    Requested = 'requested',
    Rejected = 'rejected',
    Accepted = 'accepted',
    OnHold = 'on_hold'
}

// Entities

export type ResponseOrgTransactions = {
    transactions: Partial<Record<UserProfile['id'], { status?: OrgTransactionStatus; isUnlocked: boolean }>>
    metadata: Record<OrgTransactionStatus, number>
}

export type OrgTransactionEntityTalentBaseUserShow = {
    type: 'talent_base_user_show'
    id: UserId
    message: string
    rejectReason?: string
    status: OrgTransactionStatus
}

export type OrgTransactionEntityJobPromote = {
    type: 'job_fee'
    id: JobId
}

export type OrgTransactionPaymentEntity = OrgTransactionEntityTalentBaseUserShow | OrgTransactionEntityJobPromote

export type OrgTransactionPaymentEntityType = OrgTransactionPaymentEntity['type']

// Common

export type OrgTransactionCommon = {
    orgId: OrgId
    createdByAdminId?: UserId
    /** Set on transactions in euro credits (CREDIT_UNIT); older amounts are 100 € credits until migrated. */
    creditUnit?: typeof CREDIT_UNIT
}

// Types

export type OrgTransactionDeposit = {
    type: 'deposit'
    amount: number
    woocommerceOrderId?: number
    /** WooCommerce deposits since Oct 2026: `amount` = paidCredits (= € paid, without VAT) + bonusCredits. */
    paidCredits?: number
    bonusCredits?: number
}

export type OrgTransactionPayment = {
    type: 'payment'
    amount: number
    entity: OrgTransactionPaymentEntity
}

export type OrgTransactionPaymentInput = {
    amount: number
    entity:
        | Pick<OrgTransactionEntityTalentBaseUserShow, 'type' | 'id' | 'message'>
        | Pick<OrgTransactionEntityJobPromote, 'type' | 'id'>
}

export type OrgTransactionData = OrgTransactionDeposit | OrgTransactionPayment

// Main

export type OrgTransaction = {
    id: OrgTransactionId
} & OrgTransactionCommon &
    OrgTransactionData &
    DocumentWithTimestamps &
    DocumentWithAuthor

export type OrgTransactionPaymentResponse = {
    id: OrgTransactionId
} & OrgTransactionCommon &
    OrgTransactionPayment &
    DocumentWithTimestamps &
    DocumentWithAuthor

export type OrgTransactionInput = OrgTransactionCommon & OrgTransactionData

export type OrgTransactionWithAuthor = OrgTransaction & {
    user: UserProfile
    admin?: UserProfile
}

export type OrgPaymentTransactionWithOrg = OrgTransactionPaymentResponse & {
    org: Org
}

/** What a candidate gets about an org's reveal request: what the request page shows of the org. */
export type TalentBaseRequestOrg = Pick<
    Org,
    'id' | 'slug' | 'title' | 'image' | 'desc' | 'type' | 'galleryImages' | 'galleryVideos'
>

/**
 * A reveal request as the candidate sees it. No price, no member who sent it, no org internals
 * (members, credit users, billing details) and not the candidate's own profile document.
 */
export type TalentBaseRequestForTalent = {
    id: OrgTransactionId
    createdAt: OrgTransactionPaymentResponse['createdAt']
    updatedAt: OrgTransactionPaymentResponse['updatedAt']
    entity: Pick<OrgTransactionEntityTalentBaseUserShow, 'type' | 'status' | 'message'>
}

export type OrgTransactionFull = {
    transaction: TalentBaseRequestForTalent
    org: TalentBaseRequestOrg
}

export type OrgAllowedCreditUser = {
    userId: UserId
    type: OrgTransactionPaymentEntityType[]
}

export type OrgTransactionBalance = {
    id: OrgId
    income: number
    expense: number
    reserved: number
    balance: number
    accepted: number
    on_hold: number
}

export type OrgWithTransactionBalance = {
    org: Org
    balance?: Omit<OrgTransactionBalance, 'id'>
}

// WooCommerce

export type WooCommerceTokenPayload = {
    'order_id': number
    'user_id': number
    'old_status'?: string
    'new_status'?: string
    'csus_id': UserId
    'org_id': OrgId
    'products': {
        'product_id': number
        'quantity': number
    }[]
}

export type WooCommerceRedirectTokenPayload = {
    'status': string
    'method': TPaymentMethod
    'csus_id': UserId
    'org_id': OrgId
    'order_id'?: number
    'products': {
        'product_id': number
        'quantity': number
    }[]
}

// Payment

export type PaymentSuccessData = {
    status: string
    method: TPaymentMethod
    amount: number
    orgTitle: string
    orgSlug: string
    orgId: OrgId
}
